import { useState } from 'react';
import { requestMagicLink, founderEmail } from '@/lib/auth';
import { firebaseConfigured } from '@/firebase';

// Sign-in surface for the founder console. Same magic-link pattern as
// rta-chat, but the copy is console-specific and the form tints slightly
// cooler because the canvas is cream, not lavender.
export function SignInCard() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setState('sending');
    setError(null);
    try {
      if (!firebaseConfigured) {
        throw new Error(
          'Firebase is not configured for this build. Populate .env.local and rebuild.',
        );
      }
      await requestMagicLink(email.trim());
      setState('sent');
    } catch (err) {
      setError((err as Error).message);
      setState('error');
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <form onSubmit={submit} className="glass w-full max-w-sm space-y-4 p-6 text-center">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-ink-400">rta.systems</div>
          <h1 className="mt-1 text-2xl font-semibold text-ink-900">Founder console</h1>
          <p className="mt-1 text-sm text-ink-600">
            Sign in with your founder email to manage Factory runs.
          </p>
        </div>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={founderEmail()}
          className="w-full rounded-xl border border-white/70 bg-white/70 px-3 py-2 text-sm outline-none placeholder:text-ink-400 focus:border-accent-300 focus:ring-2 focus:ring-accent-300/50"
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className="w-full rounded-xl bg-accent-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-accent-700 disabled:opacity-60"
        >
          {state === 'sending' ? 'Sending…' : 'Send me a magic link'}
        </button>
        {state === 'sent' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
            Magic link sent to <b>{email}</b>. Check your inbox.
          </div>
        )}
        {state === 'error' && error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
        {!firebaseConfigured && state === 'idle' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800">
            Firebase env not populated — magic-link sending will fail until{' '}
            <code>.env.local</code> is filled out. Scaffold UI is still browsable.
          </div>
        )}
      </form>
    </div>
  );
}
